<template>
  <PageLayout title="Tasks" class="relative z-0">
    <template #header>
      <PageHeader v-if="!maxSmall" :title="t('admin.navigation.tasks.label')">
        <template v-if="!isLoadingViewType" #actions>
          <Actions />
        </template>
        <template v-if="!isLoadingViewType" #views-bar>
          <ViewsBar
            ref="viewsBar"
            type="tasks"
            :views="views"
            :viewType="viewType"
            :currentView="currentView"
            @selectView="handleSelectView"
            @deleteView="handleDeleteView"
            @addView="handleAddView"
            @renameView="handleRenameView"
            @updateView="handleUpdateView"
          />
        </template>
      </PageHeader>
    </template>
    <template v-if="!isLoadingViewType">
      <TasksOverview
        :count="count"
        :fetchingStatus="fetchingStatus"
        @add:task="handleAddTask"
        @show:detail="navigate($event)"
        @update:count="count = $event"
        @reset="handleReset"
        @reload="handleReload"
        @update:fetchingStatus="handleUpdateFetchingStatus"
      />
      <Show v-if="task" :task="task" @close="navigate" />
    </template>
    <div v-else class="h-full flex items-center justify-center">
      <LoadingSpinner variant="primary" size="lg" />
    </div>
  </PageLayout>
</template>

<script setup>
// Libraries
import route from "ziggy-js";
import { router, usePage } from "@inertiajs/vue3";
import { useI18n } from "vue-i18n";
import { computed, onMounted, onUnmounted, ref, watch, inject } from "vue";

// Layout imports
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import PageLayout from "@/Layouts/PageLayout.vue";

// Shared components imports
import { LoadingSpinner } from "@/SharedComponents/LoadingSpinner";
import { PageHeader } from "@/SharedComponents/PageHeader";
import { ViewsBar } from "@/SharedComponents/PageHeader/Partials";

// Page specific components imports
import Actions from "@/Pages/Tasks/Partials/Header/Actions.vue";
import TasksOverview from "@/Pages/Tasks/TasksOverview.vue";

// Composable imports
import { default as Show } from "@/Pages/Tasks/Details/TaskDetails.vue";
import { useFilters } from "@/Composables/useFilters";
import { useUserPreferences } from "@/Composables/useUserPreferences";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { toast } from "@/SharedComponents/Toaster";

// Views and tasks imports
import { useDefaultTasksViews } from "@/Pages/Tasks/Views";
import { useTasksViews } from "@/Composables/useTasksViews";
import { useViewControls } from "@/Composables/useViewControls";
import { storeToRefs } from "pinia";

// Define Options
defineOptions({ layout: PersistentLayout });

// Define Props
defineProps({
  task: { type: Object, required: false },
});

const { customViews, isLoading: isLoadingCustomViews } = useTasksViews();
const { defaultViews, DefaultView } = useDefaultTasksViews();

// Breakpoints
const { maxSmall } = useBreakpoints();

// Emitter
const emitter = inject("emitter");

// Translations
const { t } = useI18n();

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// View Controls: Global
const viewControlsStore = useViewControls("viewControls");
const { handleUpdateLoading } = viewControlsStore;

// Filters: Tasks
const filterStore = useFilters("tasks");
const { handleInitialiseFilters, handleClearFilters, handleSwitchView } =
  filterStore;

// Task View
const { isLoading: isLoadingViewType, data: viewType } =
  useUserPreferences("task_view");

// Refs
const views = ref(defaultViews);
const count = ref(null);
const fetchingStatus = ref("loading");
const viewsBar = ref(null);
const activeView = ref(DefaultView.ALL);

// Computed Props
const currentView = computed(() => {
  if (activeView.value.default) {
    return {
      ...activeView.value,
      name: activeView.value.name,
    };
  }

  return activeView.value;
});

// Functions
const handleReset = () => {
  if (maxSmall.value) {
    emitter.emit("reset-filters");
  } else {
    viewsBar.value.reset();
  }
};

const handleReload = () => {
  window.location.reload();
};

const handleUpdateFetchingStatus = (value) => {
  fetchingStatus.value = value;
};

const navigate = (task) => {
  const url = task ? route("tasks.show", [task]) : route("tasks.index");

  router.visit(url, {
    preserveState: true,
    preserveScroll: true,
  });
};

const handleSelectView = (view) => {
  handleSwitchView(view);
  activeView.value = view;

  if (!view.default) {
    handleInitialiseFilters(view.criteria);
    return;
  }

  const defaultFilters = view.filters.getActiveFilterKeys();
  handleInitialiseFilters(view.filters, defaultFilters);
};

const handleDeleteView = (viewId) => {
  views.value = views.value.filter((item) => item.id !== viewId);
};

const handleAddView = (view) => {
  view.default = false;
  views.value.push(view);
  handleSelectView(view);
};

const handleRenameView = (view) => {
  views.value[views.value.findIndex((item) => item.id === view.id)] = view;
};

const handleUpdateView = (view) => {
  activeView.value = view;
  views.value[views.value.findIndex((item) => item.id === view.id)] = view;
};

const handleAddTask = () => {
  router.visit(route("tasks.add"));
};

// Watchers
watch(fetchingStatus, () => {
  handleUpdateLoading(fetchingStatus.value !== "loaded");
});

watch(
  () => globalFilterValues.value.tenant,
  () => {
    handleSelectView(DefaultView.ALL);
  }
);

// Lifecycle Hooks
onMounted(() => {
  const flash = usePage().props.flash;

  if (flash) {
    toast.success(t(flash));
  }

  if (!isLoadingCustomViews.value) {
    views.value = defaultViews.concat(customViews.value);
  }

  watch(
    isLoadingCustomViews,
    (newValue) => {
      if (!newValue) {
        views.value = defaultViews.concat(customViews.value);
      }
    },
    {
      deep: true,
    }
  );
});

onUnmounted(() => {
  handleClearFilters();
  handleSelectView(DefaultView.ALL);
});
</script>
